import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card, CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { UploadS3Input } from "design-system/organisms/UploadS3Input"
import { getErrorTranslations } from "lib/i18n"
import { Bold, Italic, Plus, Sparkles, Strikethrough, Trash } from "lucide-react"
import { useImproveWhatsappTemplateMutation } from "modules/campaigns/hooks/useImproveWhatsappTemplateMutation"
import { useUploadWhatsappMediaMutation } from "modules/campaigns/hooks/useUploadWhatsappMediaMutation"
import { Loading } from "modules/shared/components/Loading"
import { useLanguageOptions } from "modules/shared/hooks/useLanguageLabel"
import { useRef } from "react"
import { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateWhatsappTemplateSchema } from "schema"
import { z } from "zod"
import { WhatsappTemplatePreview } from "../WhatsappTemplatePreview"
import { WhatsappTemplateButtonsField } from "./WhatsappTemplateButtonsField"
import { WhatsappTemplateTextarea } from "./WhatsappTemplateTextarea"

type WhatsappMessageCreatorProps = {
  form: UseFormReturn<z.infer<typeof CreateWhatsappTemplateSchema>>
  mode: "create" | "view" | "edit"
  onSubmit?: SubmitHandler<z.infer<typeof CreateWhatsappTemplateSchema>>
  onDelete?: () => void
  isPending?: boolean
}

function insertSymbolAtPositions(str: string, start: number, end: number, symbol: string): string {
  const withEndInserted = str.slice(0, end) + symbol + str.slice(end)
  const withBothInserted = withEndInserted.slice(0, start) + symbol + withEndInserted.slice(start)
  return withBothInserted
}

export const WhatsappMessageCreator = ({
  form,
  onSubmit,
  mode,
  onDelete,
  isPending,
}: WhatsappMessageCreatorProps) => {
  const { t } = useTranslation()
  const languageOptions = useLanguageOptions()
  const formValues = form.watch()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const { mutateAsync: uploadWhatsappMedia, isPending: isUploadWhatsappMediaPending } =
    useUploadWhatsappMediaMutation()
  const { mutateAsync: improveTemplate, isPending: isImproveTemplatePending } =
    useImproveWhatsappTemplateMutation()

  function extractVariablesFromBody(
    body: string,
    currentVariables: z.infer<
      typeof CreateWhatsappTemplateSchema.shape.template.shape.whatsappTemplateVariables
    > = [],
  ) {
    let updatedBody = body
    const variableNames = body.match(/{{(\w+)}}/g)?.map((name) => name.replace(/{{|}}/g, "")) || []

    // Create cleaned variables array from valid variables in body
    const cleanedVariables = variableNames.map((name) => {
      const existingVariable = currentVariables.find((v) => v.paramName === name)
      const cleanedName = name.toLowerCase().replace(/\s/, "_").replace(/\W+/, "")

      // Replace the variable name in the body with the cleaned version
      if (cleanedName !== name) {
        updatedBody = updatedBody.replace(`{{${name}}}`, `{{${cleanedName}}}`)
      }

      return {
        paramName: cleanedName,
        example: existingVariable?.example || "",
        description: existingVariable?.description ?? "",
      }
    })

    return { cleanedVariables, updatedBody }
  }

  const updateBody = (body: string) => {
    const formValues = form.watch()
    const currentVariables = formValues.template.whatsappTemplateVariables || []
    const { cleanedVariables, updatedBody } = extractVariablesFromBody(body, currentVariables)

    // Update form with cleaned variables (this removes any variables not found in body)
    form.setValue("template.whatsappTemplateVariables", cleanedVariables)
    form.setValue("template.body", updatedBody)
  }

  const formatText = (action: "bold" | "strike" | "italic") => {
    const position = checkSelection()
    if (!position) return

    const textareaText = formValues.template.body
    const wrapSymbols = {
      bold: "*",
      strike: "~",
      italic: "_",
    }[action]

    form.setValue(
      "template.body",
      insertSymbolAtPositions(textareaText, position.start, position.end, wrapSymbols),
    )
  }

  const addVariable = () => {
    const textareaText = formValues.template.body
    const variablesCount = formValues.template.whatsappTemplateVariables?.length ?? 0
    const paramName = `variable_${variablesCount + 1}`
    const position = checkSelection() ?? { start: textareaText.length, end: textareaText.length }
    const body =
      textareaText.slice(0, position.start) + `{{${paramName}}}` + textareaText.slice(position.end)

    form.setValue("template.whatsappTemplateVariables", [
      ...(formValues.template.whatsappTemplateVariables ?? []),
      { paramName, example: "", description: "" },
    ])

    form.setValue("template.body", body)
  }

  const removeVariable = (index: number) => {
    const variable = formValues.template.whatsappTemplateVariables?.[index]

    if (!variable) return

    const newVariables = formValues.template.whatsappTemplateVariables?.filter(
      (_, i) => i !== index,
    )
    form.setValue("template.whatsappTemplateVariables", newVariables)

    const body = formValues.template.body
    const newBody = body.replace(`{{${variable.paramName}}}`, "")
    form.setValue("template.body", newBody)
  }

  const updateVariable = (
    index: number,
    variable: NonNullable<
      z.infer<typeof CreateWhatsappTemplateSchema>["template"]["whatsappTemplateVariables"]
    >[number],
  ) => {
    const oldVariable = { ...formValues.template.whatsappTemplateVariables?.[index] }

    const newVariables = formValues.template.whatsappTemplateVariables?.map((v, i) =>
      i === index
        ? {
            ...variable,
            paramName: variable.paramName.toLowerCase().replace(/\s/, "_").replace(/\W+/, ""),
          }
        : v,
    )
    form.setValue("template.whatsappTemplateVariables", newVariables)

    const newBody = formValues.template.body.replace(
      `{{${oldVariable.paramName}}}`,
      `{{${variable.paramName}}}`,
    )

    form.setValue("template.body", newBody)
  }

  const checkSelection = () => {
    const textarea = textareaRef.current

    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd

      return { start, end }
    }
  }

  const onImproveWhatsappTemplate = async () => {
    const { template } = form.watch()

    const response = await improveTemplate({
      body: template.body,
      language: template.language,
    })
    form.setValue("template.body", response.body)
  }

  const isSubmitDisabled = isPending || form.formState.disabled || mode === "view"
  const missingLanguage = !form.watch().template.language

  return (
    <div className="grid lg:grid-cols-6 grid-cols-1 gap-3">
      <Card className="col-span-4 p-4 flex flex-col gap-8 relative overflow-hidden">
        <div className="flex flex-col gap-1">
          <CardTitle>{t("campaigns.templates.whatsapp.detail.config.title")}</CardTitle>
          <CardDescription>
            {t("campaigns.templates.whatsapp.detail.config.description")}
          </CardDescription>
        </div>
        <div>
          <Form
            {...form}
            errorTranslations={getErrorTranslations()}
            onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}
            className="flex flex-col gap-4"
          >
            <div className="grid lg:grid-cols-6 grid-cols-1 gap-4 items-start">
              <FormField
                control={form.control}
                name="template.name"
                render={({ field }) => (
                  <FormItem className="col-span-6 lg:col-span-4">
                    <FormLabel>
                      {t("campaigns.templates.whatsapp.detail.config.blocks.name")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        required
                        disabled={mode === "edit" || mode === "view"}
                        onChange={(ev) => {
                          form.setValue(
                            "template.name",
                            ev.target.value.toLowerCase().replace(/\s/, "_").replace(/\W+/, ""),
                          )
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="template.language"
                render={({ field }) => (
                  <FormItem className="col-span-6 lg:col-span-2">
                    <FormLabel>
                      {t("campaigns.templates.whatsapp.detail.config.blocks.language")}
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        required
                        value={field.value}
                        onValueChange={(value) => {
                          form.setValue("template.language", value)
                        }}
                        disabled={mode === "edit" || mode === "view"}
                      >
                        <SelectTrigger {...field}>
                          <SelectValue
                            placeholder={t(
                              "campaigns.templates.whatsapp.detail.config.blocks.language",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {languageOptions.map((language) => (
                              <SelectItem key={language.value} value={language.value}>
                                {language.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="media.url"
              render={() => (
                <FormItem className="col-span-6 lg:col-span-4">
                  <FormLabel>
                    {t("campaigns.templates.whatsapp.detail.config.blocks.media")}
                  </FormLabel>
                  <FormControl>
                    <UploadS3Input
                      onChange={(media) => {
                        if (media.url) {
                          form.setValue("media", media)
                        } else {
                          form.setValue("media", null)
                        }
                      }}
                      placeholder={t(
                        "campaigns.templates.whatsapp.detail.config.blocks.mediaPlaceholder",
                      )}
                      mutateAsync={uploadWhatsappMedia}
                      isPending={isUploadWhatsappMediaPending}
                      disabled={mode === "view"}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="template.body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("campaigns.templates.whatsapp.detail.config.blocks.body")}
                  </FormLabel>
                  <FormControl>
                    <>
                      <div className="relative">
                        <WhatsappTemplateTextarea
                          showErrorMessage={false}
                          required
                          rows={40}
                          {...field}
                          value={formValues.template.body}
                          ref={(e) => {
                            form.register(field.name).ref(e)
                            textareaRef.current = e
                          }}
                          onChange={(value) => {
                            updateBody(value)
                          }}
                          disabled={isImproveTemplatePending || mode === "view"}
                          className="min-h-32"
                        >
                          <Tooltip>
                            <TooltipTrigger className="absolute! bottom-8 right-2" asChild>
                              <Button
                                variant="gradient"
                                size="sm"
                                disabled={
                                  isImproveTemplatePending || isSubmitDisabled || missingLanguage
                                }
                                onClick={onImproveWhatsappTemplate}
                                type="button"
                                className="disabled:pointer-events-auto"
                              >
                                <Sparkles className="size-4" />
                                <span>
                                  {t(
                                    "campaigns.templates.whatsapp.detail.config.blocks.improveWithAI",
                                  )}
                                </span>
                              </Button>
                            </TooltipTrigger>
                            {missingLanguage && (
                              <TooltipContent>
                                {t(
                                  "campaigns.templates.whatsapp.detail.config.blocks.missingLanguage",
                                )}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </WhatsappTemplateTextarea>
                      </div>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          type="button"
                          variant={"outline"}
                          onClick={() => formatText("bold")}
                          title={t("campaigns.templates.whatsapp.detail.config.bodyActions.bold")}
                          disabled={mode === "view"}
                        >
                          <Bold />
                        </Button>
                        <Button
                          size="icon"
                          type="button"
                          variant={"outline"}
                          onClick={() => formatText("strike")}
                          title={t("campaigns.templates.whatsapp.detail.config.bodyActions.strike")}
                          disabled={mode === "view"}
                        >
                          <Strikethrough />
                        </Button>
                        <Button
                          size="icon"
                          type="button"
                          variant={"outline"}
                          onClick={() => formatText("italic")}
                          title={t("campaigns.templates.whatsapp.detail.config.bodyActions.italic")}
                          disabled={mode === "view"}
                        >
                          <Italic />
                        </Button>
                        <Button
                          type="button"
                          variant={"outline"}
                          onClick={addVariable}
                          title={t(
                            "campaigns.templates.whatsapp.detail.config.bodyActions.addVariable",
                          )}
                          disabled={mode === "view"}
                        >
                          <Plus />
                          <span>
                            {t(
                              "campaigns.templates.whatsapp.detail.config.bodyActions.addVariable",
                            )}
                          </span>
                        </Button>
                      </div>
                    </>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {formValues.template.whatsappTemplateVariables &&
              formValues.template.whatsappTemplateVariables?.length > 0 && (
                <FormField
                  control={form.control}
                  name="template.whatsappTemplateVariables"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Tooltip>
                          <TooltipTrigger asChild slick>
                            <span>
                              {t(
                                "campaigns.templates.whatsapp.detail.config.blocks.templateVariables",
                              )}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>
                              {t(
                                "campaigns.templates.whatsapp.detail.config.blocks.variableTooltip",
                              )}
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <FormControl>
                        <div className="flex flex-col gap-2">
                          {field.value?.map((variable, index) => (
                            <div
                              className="flex flex-row justify-between items-start gap-2"
                              key={variable.paramName}
                            >
                              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <Label className="text-muted-foreground">
                                  {t(
                                    "campaigns.templates.whatsapp.detail.config.blocks.templateVariableName",
                                  )}
                                </Label>
                                <Input
                                  disabled={mode === "view"}
                                  value={variable.paramName}
                                  onChange={(ev) => {
                                    updateVariable(index, {
                                      ...variable,
                                      paramName: ev.target.value,
                                    })
                                  }}
                                />
                              </div>
                              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <Label className="text-muted-foreground">
                                  {t(
                                    "campaigns.templates.whatsapp.detail.config.blocks.templateVariableExample",
                                  )}
                                </Label>
                                <Input
                                  disabled={mode === "view"}
                                  value={variable.example}
                                  onChange={(ev) => {
                                    updateVariable(index, {
                                      ...variable,
                                      example: ev.target.value,
                                    })
                                  }}
                                />
                              </div>
                              <FormField
                                control={form.control}
                                name={`template.whatsappTemplateVariables.${index}.description`}
                                render={({ field: descriptionField }) => (
                                  <FormItem className="min-w-0 flex-[2]">
                                    <FormLabel className="text-muted-foreground">
                                      {t(
                                        "campaigns.templates.whatsapp.detail.config.blocks.templateVariableDescription",
                                      )}
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        {...descriptionField}
                                        value={descriptionField.value ?? ""}
                                        placeholder={t(
                                          "campaigns.templates.whatsapp.detail.config.blocks.templateVariableDescriptionPlaceholder",
                                        )}
                                        disabled={mode === "view"}
                                        required
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <Button
                                size="icon"
                                className="mt-5 shrink-0"
                                type="button"
                                variant="outline"
                                onClick={() => removeVariable(index)}
                                disabled={mode === "view"}
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            <WhatsappTemplateButtonsField form={form} isDisabled={mode === "view"} />
            <div className="flex justify-end gap-2">
              {onDelete && (
                <Button type="button" variant="destructive" onClick={onDelete}>
                  {t("campaigns.templates.whatsapp.detail.config.actions.delete.action")}
                </Button>
              )}
              <Button type="submit" disabled={isSubmitDisabled}>
                {form.formState.disabled ? (
                  <Loading minimal />
                ) : (
                  t("campaigns.templates.whatsapp.detail.config.actions.save.action")
                )}
              </Button>
            </div>
          </Form>
        </div>
      </Card>
      <WhatsappTemplatePreview
        header={formValues.template.header}
        body={formValues.template.body}
        footer={formValues.template.footer}
        variables={formValues.template.whatsappTemplateVariables ?? []}
        className="lg:col-span-2 col-span-4"
        media={formValues.media}
        buttonTexts={formValues.template.buttons?.map((button) => button.text)}
      />
    </div>
  )
}
